import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, ArrowRight, Truck, Tag, X, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { useCart } from '../context/CartContext';
import { createCheckoutSession, validatePromoCode } from '../lib/api';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [email, setEmail] = useState('');

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  useSEO({ title: 'Your Cart', description: 'Review your NestFlow cart and checkout.' });

  const discount = appliedPromo ? appliedPromo.discount_amount : 0;
  const shipping = cartTotal >= 50 ? 0 : 5.99;
  const total = Math.max(cartTotal - discount + shipping, 0);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const result = await validatePromoCode(promoInput.trim(), cartTotal);
      setAppliedPromo(result);
      toast.success(`Promo code applied: ${result.label}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid promo code';
      toast.error(msg);
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setIsCheckingOut(true);
    try {
      const originUrl = window.location.origin;
      const response = await createCheckoutSession(
        cartItems,
        originUrl,
        appliedPromo?.code || null,
        email.trim() || null
      );
      
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1C] mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Continue Shopping
          </button>

          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-8">
            Your Cart
          </h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFAFA] rounded-2xl">
              <ShoppingBag className="w-16 h-16 text-[#C9C3B8] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-2xl font-semibold text-[#1A1A1C] font-['Manrope'] mb-2">
                Your cart is empty
              </h2>
              <p className="text-[#888] mb-6">Add some products to get started</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-[#1A1A1C] text-white px-8 py-4 rounded-full font-medium hover:bg-[#333] transition-all"
                data-testid="browse-products-btn"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.product.id}
                    className="bg-[#FAFAFA] rounded-2xl p-6 flex gap-6 group"
                    data-testid={`cart-item-${item.product.id}`}
                  >
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden bg-[#F0F0F0] flex-shrink-0">
                      <img src={item.product.image_url} alt={item.product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="text-xs font-medium uppercase tracking-wide text-[#888]">{item.product.category}</span>
                          <h3 className="font-semibold text-[#1A1A1C] font-['Manrope']">{item.product.name}</h3>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="p-2 text-[#888] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-lg transition-colors"
                          data-testid={`remove-item-${item.product.id}`}
                          aria-label="Remove item"
                        >
                          <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center border border-[#E5E5E5] rounded-lg bg-white">
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="p-2 hover:bg-[#F5F5F5] rounded-l-lg transition-colors"
                            data-testid={`decrease-qty-${item.product.id}`}
                          >
                            <Minus className="w-4 h-4 text-[#555]" strokeWidth={1.5} />
                          </button>
                          <span className="px-4 py-2 font-medium text-[#1A1A1C] min-w-[3rem] text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="p-2 hover:bg-[#F5F5F5] rounded-r-lg transition-colors"
                            data-testid={`increase-qty-${item.product.id}`}
                          >
                            <Plus className="w-4 h-4 text-[#555]" strokeWidth={1.5} />
                          </button>
                        </div>
                        <span className="font-bold text-[#1A1A1C] text-lg">${(item.product.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={clearCart}
                  className="text-[#888] hover:text-[#FF3B30] text-sm font-medium transition-colors"
                  data-testid="clear-cart-btn"
                >
                  Clear Cart
                </button>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-[#FAFAFA] rounded-2xl p-6 sticky top-28">
                  <h2 className="text-xl font-semibold text-[#1A1A1C] font-['Manrope'] mb-6">
                    Order Summary
                  </h2>

                  {/* Promo Code */}
                  <div className="mb-6">
                    {appliedPromo ? (
                      <div className="flex items-center justify-between bg-[#34C759]/10 px-4 py-3 rounded-xl" data-testid="applied-promo">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-[#34C759]" strokeWidth={2} />
                          <span className="font-semibold text-[#34C759] text-sm">{appliedPromo.code}</span>
                          <span className="text-xs text-[#34C759]">({appliedPromo.label})</span>
                        </div>
                        <button
                          onClick={handleRemovePromo}
                          className="p-1 text-[#34C759] hover:text-[#FF3B30] transition-colors"
                          data-testid="remove-promo-btn"
                        >
                          <X className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Promo code"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                          className="flex-1 bg-white border-[#E5E5E5] rounded-xl text-sm font-mono"
                          data-testid="promo-code-input"
                        />
                        <button
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoInput.trim()}
                          className="px-4 bg-[#1A1A1C] text-white rounded-xl text-sm font-medium hover:bg-[#333] transition-all disabled:opacity-40"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? '...' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-[#888] text-sm">
                      <span>Subtotal</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                    {appliedPromo && (
                      <div className="flex justify-between text-[#34C759] text-sm font-medium">
                        <span>Discount ({appliedPromo.code})</span>
                        <span>-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[#888] text-sm">
                      <span>Shipping</span>
                      <span>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    {shipping === 0 && (
                      <div className="flex items-center gap-2 text-xs text-[#34C759] bg-[#34C759]/10 px-3 py-2 rounded-lg">
                        <Truck className="w-4 h-4" strokeWidth={1.5} />
                        Free shipping applied!
                      </div>
                    )}
                    <div className="h-px bg-[#E5E5E5]" />
                    <div className="flex justify-between font-bold text-[#1A1A1C] text-lg">
                      <span>Total</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Email for confirmation */}
                  <div className="mb-5">
                    <label className="text-xs font-medium text-[#888] mb-1.5 block">Email for order confirmation</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-white border-[#E5E5E5] rounded-xl text-sm"
                      data-testid="checkout-email-input"
                    />
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={isCheckingOut || cartItems.length === 0}
                    className="w-full bg-[#1A1A1C] text-white py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#333] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    data-testid="checkout-btn"
                  >
                    {isCheckingOut ? 'Processing...' : (
                      <>
                        Proceed to Checkout
                        <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-[#888] mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default CartPage;
